<template>
    <div class="background">
        <div class="header-wrapper">
            <LabelInput
                label="Name"
                placeholder="CV map name (up to 40 characters)"
                uniqueId="cvName"
                class="cv-name"
                :maxlength="40"
                :boldOnFocusOut="true"
                :isInvalid="$v.localName.$invalid && !!nameValidationError"
                v-model="$v.localName.$model"
                ref="name"
                @input="$emit('update:name', localName)">
                <template #inputIcon>
                    <Tooltip v-if="$v.localName.$invalid && nameValidationError"
                             :text="nameValidationError"
                             class="validation-icon">
                            <IconBase iconName="notificationErrorIcon"
                                      :width="12"
                                      :height="12"
                                      verticalAlign="middle"
                                      :iconColor="consts.COLORS.NEGATIVE_500"
                            />
                    </Tooltip>
                </template>
            </LabelInput>
            <span class="apps-status-row">
                <AppsDropdown
                    class="cv-apps"
                    :cvMapId="cvMapId"
                    :selectedIds="selectedApps"
                    :isLoading="appsLoading"
                    :selectedLoaded="selectedLoaded"
                    @openMenu="showSdkSetCvSwitcher = false"
                    @closeMenu="showSdkSetCvSwitcher = true"
                    @dropdownSelectedOptions="handleSelectedOptionsEvent"
                    :options="apps">
                </AppsDropdown>
                <Switcher
                    class="small sdk-set-cv font-7"
                    v-if="showSdkSetCvSwitcher"
                    uniqueId="sdkSetCvSwitcher"
                    text="Enable SDK to set CV"
                    :isActive="enableSDKSetCV"
                    inactiveSwitcherIcon="offToggleIcon"
                    activeSwitcherIcon="liveToggleIcon"
                    @changed="$emit('update:enableSDKSetCV', $event)"
                />
                <Tooltip text="ironSource SDK will automatically set the CV value in Apple’s Store Kit"
                         v-if="showSdkSetCvSwitcher" class="enable-sdk-set-cv-tooltip"/>
            </span>
            <div class="cv-header-actions">
                <button @click="$emit('cancel')" class="secondary medium">Cancel</button>
                <button @click="handleSave" class="save-button primary medium">Save CV map</button>
                <ImportCV class="import-csv" @importSteps="$emit('importSteps', $event)"/>
            </div>
        </div>
    </div>
</template>

<script>
import AppsDropdown from '@/pages/conversionValue/cvManager/components/AppsDropdown.vue';
import monetizeAppService from 'services/monetizeApp';
import LabelInput from 'components/inputs/LabelInput.vue';
import Switcher from 'components/Switcher.vue';
import consts from 'services/metaData/consts';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import IconBase from 'components/IconBase.vue';
import validators from 'services/libWrappers/vuelidate/validators';

import {
    getErrorMessageForValue,
    MANDATORY_FIELD_ERROR,
    MIN_ERROR,
    UNIQUE_ERROR
} from 'services/metaData/errors';
import { apiKey, apiPath } from 'pages/conversionValue/cvManager/serviceUtils';
import { mapActions } from 'vuex';
import ImportCV from './ImportCV.vue';

const { required, minLength } = validators;
export default {
    name: 'CVManagerEditHeader',
    components: {
        Tooltip,
        LabelInput,
        AppsDropdown,
        Switcher,
        IconBase,
        ImportCV
    },
    props: {
        name: {
            type: String,
            default: ''
        },
        cvMapId: [Number, String],
        selectedApps: {
            type: Array,
            default: () => []
        },
        enableSDKSetCV: Boolean,
        selectedLoaded: Boolean
    },
    data: () => ({
        isNameSelected: false,
        showSdkSetCvSwitcher: true,
        localName: '',
        apps: [],
        appsLoading: false,
        consts,
        schemas: [],
        nameMandatoryError: getErrorMessageForValue(MANDATORY_FIELD_ERROR, 'CV map name'),
        nameMinLengthError: getErrorMessageForValue(MIN_ERROR, '2'),
        nameUniqueError: getErrorMessageForValue(UNIQUE_ERROR, 'CV map name')
    }),
    async created() {
        this.appsLoading = true;
        monetizeAppService.getMonetizeApplications({
            filters: { liveInStore: 1, platform: 'ios', excludeArchive: true },
            extraFields: ',cvMapId'
        }).then((res) => {
            const { data: { applications } } = res;
            this.apps = applications;
            this.appsLoading = false;
        }).catch((error) => {
            this.appsLoading = false;
            const noData = error?.response?.data?.code === 404;
            const errorMessage = noData ? null : error?.response?.data?.error;
            this.$emit('error', errorMessage);
        });
        await this.getSchemas();
    },
    mounted() {
        this.$refs.name.focus();
    },
    watch: {
        name: {
            immediate: true,
            handler() {
                this.localName = this.name;
            }
        }
    },
    methods: {
        ...mapActions({
            getWithCache: 'cache/getWithCache'
        }),
        handleSelectedOptionsEvent(changeData) {
            this.$emit('update:selectedApps', changeData.selected.map((option) => option.id));
        },
        handleSave() {
            this.$v.localName.$touch();
            this.$emit('save');
        },
        async getSchemas(refresh = false) {
            try {
                const response = await this.getWithCache({
                    api: apiKey,
                    req: apiPath,
                    refresh
                });
                this.schemas = response.data;
            } catch (error) {
                this.$emit('errorGettingSchemas', error);
            }
        }
    },
    validations: {
        localName: {
            required,
            minLength: minLength(2),
            isUnique() {
                return !this.schemas.find(
                    (schema) => schema.name === this.$v.localName.$model && schema.id !== this.cvMapId
                );
            }
        }
    },
    computed: {
        nameValidationError() {
            if (!this.$v.$anyError) { return ''; }
            if (!this.$v.localName.required && this.$v.localName.$dirty) {
                return this.nameMandatoryError;
            }
            if (!this.$v.localName.minLength) {
                return this.nameMinLengthError;
            }
            return this.nameUniqueError;
        }
    }
};
</script>

<style scoped lang="scss">
@import '~styles/buttons';
@import "~styles/styleGuide";

.background {
    background-color: $white;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}
.cv-name {
    width: 396px;
    height: 32px;
    display: block;
    margin: 0 0 4px 0;
}

.cv-apps {
    margin: 4px 14px 24px 0
}

.sdk-set-cv {
    margin: 0 3px 8px 0;
    vertical-align: text-bottom;
    /deep/ .middle {
        padding-bottom: 2px;
    }
    /deep/ input[type=checkbox]~label.inputText {
        padding-left: 6px;
    }
}
.enable-sdk-set-cv-tooltip {
    vertical-align: middle;
    display: inline-grid;
    top: 73px;
    position: absolute;
}

.cv-header-actions {
    display: flex;
    position: absolute;
    right: 0px;
    top: 40px;
    margin-right: 32px;
}

.validation-icon {
    text-align: center;
    display: inline-block;
    position: absolute;
    top: 29px;
}

.save-button {
    margin-left: 8px;
    margin-right: 8px;
}

.header-wrapper {
    padding: 24px 24px 0 24px;
    border-bottom: 1px solid $light-200;
}

.apps-status-row {
    height: 60px;
    display: inline-block;
}
</style>
